import React, { useEffect, useRef, useState } from 'react';
import { swatch } from './MoreMenu';
import { RepeatPicker, ReminderPicker, cleanRepeat } from './RepeatReminders';
import { durationLabel, firstOccurrence, isoDay } from '../../lib/quickAdd';
import { ClientPicker } from './TodoClients';

export const PRIORITIES = [
  { value: 1, label: 'P1 · Urgent' },
  { value: 2, label: 'P2 · High' },
  { value: 3, label: 'P3 · Medium' },
  { value: 4, label: 'P4 · None' }
];

const ticketLabel = (x) => `${x.ticket_number} — ${x.title || ''}`;

/*
 * How long a to-do takes (073): the lengths people actually plan in. One
 * that is not among them (set by quick add's "for 50m", say) is offered as
 * itself, so opening the editor never changes it.
 */
const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240, 360, 480];
const durationOptions = (current) => {
  const n = Number(current) || 0;
  return n && !DURATIONS.includes(n) ? [...DURATIONS, n].sort((a, b) => a - b) : DURATIONS;
};

/**
 * A time as typed - "15:30", "1530", "3pm", "3.30 pm", "9" - as 'HH:MM', or
 * null when it is not one. Always 24-hour: a native time field shows 12-hour
 * on an en-US browser, which then disagrees with every chip on the page.
 */
export function parseTime(s) {
  const t = String(s || '').trim().toLowerCase().replace(/\s+/g, '');
  if (!t) return '';
  const m = t.match(/^(\d{1,2})(?:[:.h]?(\d{2}))?(am|pm|a|p)?$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const ap = m[3] ? m[3][0] : null;
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === 'p' && h < 12) h += 12;
    if (ap === 'a' && h === 12) h = 0;
  }
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/**
 * A 24-hour time field: typed, read like quick add reads a time, and tidied
 * to "HH:MM" when it is left. `onChange` gets what is typed; a time that
 * cannot be read is marked, and the form decides what to do with it.
 */
export function TimeField({ value, onChange, label = 'Due time', name }) {
  const bad = parseTime(value) === null;
  return (
    <input name={name} type="text" inputMode="numeric" autoComplete="off" aria-label={label}
           className={`ctd-time${bad ? ' is-bad' : ''}`} aria-invalid={bad || undefined}
           placeholder="HH:MM" maxLength={8} value={value}
           onChange={(e) => onChange(e.target.value)}
           onBlur={() => { const p = parseTime(value); if (p !== null && p !== value) onChange(p); }} />
  );
}

/**
 * "For which ticket". A contractor picks from their own assignments, which
 * the portal has already loaded - the list is short and every one of them
 * is theirs. A member of staff can read hundreds of tickets, so for them the
 * picker is a search (GET /todos/tickets?q=, debounced) and the select holds
 * what it found, plus whatever the to-do is already pinned to.
 */
export function TicketPicker({ value, onChange, tickets = [], search = null, current = null }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [looking, setLooking] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!search) return undefined;
    const term = q.trim();
    if (!term) { setFound([]); return undefined; }
    const mine = ++seq.current;
    setLooking(true);
    const t = setTimeout(() => {
      search(term)
        .then((rows) => { if (mine === seq.current) setFound(Array.isArray(rows) ? rows : []); })
        .catch(() => { if (mine === seq.current) setFound([]); })
        .finally(() => { if (mine === seq.current) setLooking(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, search]);

  const options = [];
  const seen = new Set();
  const push = (x) => { if (x && x.id && !seen.has(String(x.id))) { seen.add(String(x.id)); options.push(x); } };
  if (search) { push(current); found.forEach(push); } else tickets.forEach(push);

  const select = (
    <select name="ticket_id" aria-label="For which ticket" value={value || ''}
            onChange={(e) => onChange(e.target.value)}>
      <option value="">{search && q.trim() && !found.length && !looking ? 'No ticket found' : 'Not about a ticket'}</option>
      {options.map((x) => <option key={x.id} value={x.id}>{ticketLabel(x)}</option>)}
    </select>
  );
  if (!search) return <label><span>Ticket</span>{select}</label>;
  // Staff: one control - type to find, then choose - drawn as a single field.
  return (
    <div className="ctd-field ctd-ticket-pick">
      <span className="ctd-field-label">Ticket{looking ? ' …' : ''}</span>
      <span className="ctd-ticket-box">
        <input type="search" value={q} aria-label="Find a ticket" placeholder="Find…"
               onChange={(e) => setQ(e.target.value)} />
        {select}
      </span>
    </div>
  );
}

/**
 * Which list, and which section of it. Choosing another list starts at "No
 * section". `fixedList`: an editor of somebody else's shared list may move a
 * to-do between its sections but not out of it (the server says 403).
 */
export function ListPicker({ lists, listId, sectionId, onChange, fixedList = false }) {
  const list = lists.find((l) => l.id === listId) || null;
  const sections = list && Array.isArray(list.sections) ? list.sections : [];
  return (
    <>
      <label><span>List</span>
        <select aria-label="List" value={listId || ''} disabled={fixedList}
                title={fixedList ? 'Only the owner of this list can move its to-dos out of it' : undefined}
                onChange={(e) => onChange(e.target.value || null, null)}>
          {lists.map((l) => <option key={l.id} value={l.id}>{l.is_private ? `${l.name} (private)` : l.name}</option>)}
        </select>
      </label>
      <label><span>Section</span>
        <select aria-label="Section" value={sectionId || ''} disabled={!sections.length}
                onChange={(e) => onChange(listId, e.target.value || null)}>
          <option value="">No section</option>
          {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>
    </>
  );
}

/** The labels as toggles: a chip each, pressed when the to-do carries it. */
export function LabelPicker({ labels, value, onChange }) {
  if (!labels.length) return null;
  const on = new Set(value);
  return (
    <fieldset className="tda-label-pick">
      <legend>Labels</legend>
      {labels.map((b) => (
        <label key={b.id} className={`tda-label-toggle${on.has(b.id) ? ' is-on' : ''}`}>
          <input type="checkbox" checked={on.has(b.id)} aria-label={`Label ${b.name}`}
                 onChange={(e) => onChange(e.target.checked
                   ? [...value, b.id] : value.filter((x) => x !== b.id))} />
          <span className={`tda-dot ${swatch(b.color)}`} aria-hidden="true" />
          {b.name}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * The full editor for one to-do, opened in place of its row. Everything the
 * quick-add line can say, by hand - and, with lists, which list and section
 * it is in and which labels it carries. `onSave` gets the values; the page
 * decides what to send.
 *
 * Batch 3 added how it REPEATS and when to be REMINDED (RepeatReminders.js).
 * A repeat needs a day to count from, so choosing one on a to-do with no date
 * gives it the first day the repeat falls on. A repeating to-do also gets
 * "Complete forever": ticking it only moves it on to the next time.
 */
export default function TodoEditor({ item, withLists, lists = [], labels = [], tickets = [],
                                     searchTickets = null, busy, onSave, onCancel,
                                     now = new Date(), onCompleteForever = null, fixedList = false,
                                     companies = null, isAdmin = false, plan = false, submitLabel = 'Save',
                                     variant = 'inline', liveRef = null }) {
  const panel = variant === 'panel';
  const [v, setV] = useState(() => ({
    title: item.title || '',
    due_date: item.due_date ? String(item.due_date).slice(0, 10) : '',
    due_time: item.due_time ? String(item.due_time).slice(0, 5) : '',
    priority: String(item.priority || 4),
    ticket_id: item.ticket_id || '',
    notes: item.notes || '',
    list_id: item.list_id || null,
    section_id: item.section_id || null,
    labels: Array.isArray(item.labels) ? item.labels : [],
    repeat: cleanRepeat(item.repeat),
    reminders: Array.isArray(item.reminders) ? item.reminders : [],
    // 072: the client - only where the server has clients and this is staff.
    company_id: companies ? item.company_id || '' : undefined,
    // 073: the deadline and how long it takes - only where the server has them.
    deadline: plan ? (item.deadline ? String(item.deadline).slice(0, 10) : '') : undefined,
    duration_minutes: plan ? (item.duration_minutes ? String(item.duration_minutes) : '') : undefined
  }));
  const todayIso = isoDay(now);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const current = item.ticket_id
    ? { id: item.ticket_id, ticket_number: item.ticket_number, title: item.ticket_title } : null;

  const timeOk = parseTime(v.due_time) !== null;
  // Planned for after it must be done: said, not refused (the server warns too).
  const lateByPlan = plan && v.deadline && v.due_date && v.due_date > v.deadline;
  const save = (e) => {
    if (e) e.preventDefault();
    if (!timeOk) return;
    onSave({ ...v, due_time: parseTime(v.due_time) || '' });
    // In the side panel what was just saved is the new starting point.
    if (panel) setBase(JSON.stringify(v));
  };
  /*
   * THE SIDE PANEL (Oct 2026): the same fields, saved with Save or
   * Ctrl+Enter - and, through `liveRef`, saved for the person when the panel
   * closes or steps to the next to-do, so nothing typed is lost.
   */
  const [base, setBase] = useState(() => JSON.stringify(v));
  const dirty = panel && JSON.stringify(v) !== base;
  if (liveRef) liveRef.current = { dirty, ok: timeOk && Boolean(String(v.title || '').trim()),
                                   values: { ...v, due_time: parseTime(v.due_time) || '' } };

  return (
    <form className={`ctd-edit${panel ? ' is-panel' : ''}`} onSubmit={save}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !panel) onCancel();
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); save(); }
          }}>
      {panel ? (
        <textarea name="title" className="ctd-edit-title" rows={1} value={v.title} maxLength={300} aria-label="Title"
                  onChange={(e) => setV({ ...v, title: e.target.value.replace(/\n/g, ' ') })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) { e.preventDefault(); save(); } }} />
      ) : (
        <input name="title" type="text" value={v.title} onChange={set('title')}
               aria-label="Title" maxLength={300} autoFocus />
      )}
      <div className="ctd-edit-row">
        <label><span>Due</span>
          <input name="due_date" type="date" aria-label="Due date" value={v.due_date} onChange={set('due_date')} />
        </label>
        <label><span>Time</span>
          <TimeField name="due_time" value={v.due_time} onChange={(t) => setV((prev) => ({ ...prev, due_time: t }))} />
        </label>
        <label><span>Priority</span>
          <select name="priority" aria-label="Priority" value={v.priority} onChange={set('priority')}>
            {PRIORITIES.map((p) => <option key={p.value} value={String(p.value)}>{p.label}</option>)}
          </select>
        </label>
        {withLists && (
          <ListPicker lists={lists} listId={v.list_id} sectionId={v.section_id} fixedList={fixedList}
                      onChange={(list_id, section_id) => setV({ ...v, list_id, section_id })} />
        )}
        <TicketPicker value={v.ticket_id} tickets={tickets} search={searchTickets} current={current}
                      onChange={(ticket_id) => setV({ ...v, ticket_id })} />
        {plan && (
          <label><span>Deadline</span>
            <input name="deadline" type="date" aria-label="Deadline" value={v.deadline}
                   title="The date it must be done by - apart from the day it is planned for"
                   onChange={set('deadline')} />
          </label>
        )}
        {plan && (
          <label><span>Takes</span>
            <select name="duration_minutes" aria-label="How long it takes" value={v.duration_minutes}
                    onChange={set('duration_minutes')}>
              <option value="">No estimate</option>
              {durationOptions(v.duration_minutes).map((n) => (
                <option key={n} value={String(n)}>{durationLabel(n)}</option>
              ))}
            </select>
          </label>
        )}
        {withLists && companies && (
          <ClientPicker companies={companies} value={v.company_id} current={item.company || null} isAdmin={isAdmin}
                        onChange={(company_id) => setV((prev) => ({ ...prev, company_id: company_id || '' }))} />
        )}
        <label className="ctd-grow"><span>Notes</span>
          <textarea name="notes" rows={2} maxLength={4000} aria-label="Notes" value={v.notes}
                    onChange={set('notes')} />
        </label>
      </div>
      {withLists && (
        <LabelPicker labels={labels} value={v.labels} onChange={(next) => setV({ ...v, labels: next })} />
      )}
      <div className="tdr">
        <RepeatPicker value={v.repeat} dueDate={v.due_date || null} todayIso={todayIso} now={now}
                      onChange={(repeat) => setV((prev) => ({
                        ...prev,
                        repeat,
                        due_date: repeat && !prev.due_date
                          ? firstOccurrence(repeat, now, prev.due_time || null) : prev.due_date
                      }))} />
        <ReminderPicker value={v.reminders} dueDate={v.due_date || null}
                        dueTime={timeOk && v.due_time ? parseTime(v.due_time) : null}
                        onChange={(reminders) => setV((prev) => ({ ...prev, reminders }))} />
      </div>
      {!timeOk && <p className="ctd-edit-warn" role="alert">Type the time as 15:30 or 3pm.</p>}
      {lateByPlan && (
        <p className="ctd-edit-warn is-soft" role="status" data-warn="due-after-deadline">
          It is due after its deadline - it will be late unless one of them moves.
        </p>
      )}
      {panel ? (
        <div className={`ctd-edit-buttons tdsp-save${dirty ? ' is-dirty' : ''}`} role="group" aria-label="Save changes">
          <span className="tdsp-save-note" aria-live="polite">{dirty ? 'Unsaved changes' : 'All changes saved'}</span>
          <button type="button" className="btn btn-secondary" disabled={!dirty}
                  onClick={() => setV(JSON.parse(base))}>Discard</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !timeOk || !dirty}
                  title="Save (Ctrl+Enter)">Save</button>
        </div>
      ) : (
      <div className="ctd-edit-buttons">
        {item.repeat && onCompleteForever && (
          <button type="button" className="btn btn-secondary tdr-forever" disabled={busy}
                  title="Tick it off for good: it will not come back" onClick={onCompleteForever}>
            Complete forever
          </button>
        )}
        <span className="ctd-edit-gap" />
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy || !timeOk}>{submitLabel}</button>
      </div>
      )}
    </form>
  );
}
